// Season/episode ordering, next-episode and spoiler rules (VOD02, VOD03). Owner: WP05.
import type { Episode, Progress, SeriesDetail } from '@tuned/contracts';
import { episodeCode } from '../../components/format.ts';
import { dotJoin } from '../../components/Text.tsx';
import type { PlayRequest } from '../player/machine.ts';

type SeasonGap = SeriesDetail['seasons'][number]['gaps'][number];
export type SeasonView = { number: number; label: string; isSpecials: boolean; episodes: Episode[]; gaps: SeasonGap[] };

const orderKey = (e: Episode) => e.number ?? Number.MAX_SAFE_INTEGER;

/**
 * VOD02: seasons come from the actual episode records (an episode's own `season` wins over the season it was listed
 * under), empty seasons are kept, regular seasons ascend, specials go last. Episodes sort by number; unnumbered ones go
 * last and duplicates keep provider order (stable sort) so nothing is silently dropped.
 */
export function buildSeasons(seasons: SeriesDetail['seasons']): SeasonView[] {
  const byNumber = new Map<number, SeasonView>();
  const get = (n: number) => {
    let s = byNumber.get(n);
    if (!s) byNumber.set(n, (s = { number: n, label: n === 0 ? 'Specials' : `Season ${n}`, isSpecials: n === 0, episodes: [], gaps: [] }));
    return s;
  };
  for (const season of seasons) {
    const view = get(season.number);
    if (season.isSpecials) { view.isSpecials = true; view.label = 'Specials'; }
    view.gaps.push(...season.gaps);
    for (const e of season.episodes) get(e.season).episodes.push(e);
  }
  const out = [...byNumber.values()];
  for (const s of out) s.episodes.sort((a, b) => orderKey(a) - orderKey(b));
  return out.sort((a, b) => Number(a.isSpecials) - Number(b.isSpecials) || a.number - b.number);
}

/** Gap wording: certainty only with a known complete inventory (VOD02); never a release date. */
export function gapMessage(gap: SeasonGap, inventory: SeriesDetail['inventory']): string {
  const n = gap.missingNumbers;
  const list = n.length === 1 ? `${n[0]}` : `${n.slice(0, -1).join(', ')} and ${n.at(-1)}`;
  if (gap.confirmed && inventory === 'complete') return n.length === 1 ? `Episode ${list} is missing` : `Episodes ${list} are missing`;
  return `Possible gap: ${n.length === 1 ? 'episode' : 'episodes'} ${list} not found in your provider's list`;
}

/**
 * VOD03 spoiler protection: hide the still and plot of episodes after the next-up one that have not been watched or
 * started. Watched and in-progress episodes always show.
 */
export function spoilerHidden(order: Map<string, number>, nextEpisodeId: string | null, episodeId: string, progress: Progress | null, protect: boolean): boolean {
  if (!protect || progress?.completed || (progress?.positionSec ?? 0) > 0) return false;
  return (order.get(episodeId) ?? Infinity) > (nextEpisodeId ? order.get(nextEpisodeId) ?? -1 : -1);
}

/** VOD02 autoplay: the episode right after `episodeId` in season order. Specials are never auto-played, and an unavailable next episode is not skipped over. */
export function nextEpisodeAfter(seasons: SeriesDetail['seasons'], episodeId: string): Episode | null {
  const list = buildSeasons(seasons).filter((s) => !s.isSpecials).flatMap((s) => s.episodes);
  const i = list.findIndex((e) => e.id === episodeId);
  const next = i >= 0 ? list[i + 1] : undefined;
  return next?.available ? next : null;
}

/** The player request for one episode; resumes an unfinished one (VOD03). */
export function episodeRequest(series: Pick<SeriesDetail, 'id' | 'title' | 'backdrop' | 'poster'>, e: Episode, progress: Progress | null): PlayRequest {
  return {
    sourceId: e.source.id,
    mode: 'vod',
    title: series.title,
    subtitle: dotJoin([episodeCode(e.season, e.number), e.title]),
    itemId: e.id,
    seriesId: series.id,
    positionSec: progress && !progress.completed && progress.positionSec > 0 ? progress.positionSec : 0,
    artworkId: e.still ?? series.backdrop ?? series.poster,
  };
}
